import { CancellationPolicyTag } from '@mews-distributor/common';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews/text-keys';
import { Body, Stack, Tag } from '@optimus-web/core';
import { getSelectedEnterpriseTimezone } from '../../../selectors/views/selectedEnterprise/selectedEnterprise';
import { useAppSelector } from '../../../utils/redux';
import { localizeShortDateAndTimeString } from '../../globalization/selectors';

const getTagText = (
    textKey: TextKey,
    { dateString, shortDateTimeString }: ReturnType<typeof localizeShortDateAndTimeString>
) => <LocalizedText textKey={textKey} substitutes={{ date: `${dateString} ${shortDateTimeString}` }} />;

interface RateCancellationPolicyTagProps {
    cancellationFeeEndDate: string | null;
    cancellationPolicyTag: CancellationPolicyTag | null;
}

export const RateCancellationPolicyTag = ({
    cancellationFeeEndDate,
    cancellationPolicyTag,
}: RateCancellationPolicyTagProps) => {
    const dateStrings = useAppSelector((state) => {
        const timezone = getSelectedEnterpriseTimezone(state, {});
        if (cancellationFeeEndDate == null || timezone == null) {
            return null;
        }
        return localizeShortDateAndTimeString(state, { timezone, dateTimeUtc: cancellationFeeEndDate });
    });

    if (cancellationPolicyTag == null) {
        return null;
    }

    switch (cancellationPolicyTag) {
        case CancellationPolicyTag.FreeCancellation:
            return dateStrings != null ? (
                <Stack spacing="100">
                    <Tag variant="success">
                        <LocalizedText textKey={TextKey.FreeCancellation} />
                    </Tag>
                    <Body size="small" variant="secondary">
                        {getTagText(TextKey.UntilDate, dateStrings)}
                    </Body>
                </Stack>
            ) : null;
        case CancellationPolicyTag.PartiallyRefundable:
            return dateStrings != null ? (
                <Stack spacing="100">
                    <Tag variant="info">
                        <LocalizedText textKey={TextKey.PartiallyRefundable} />
                    </Tag>
                    <Body size="small" variant="secondary">
                        {getTagText(TextKey.UntilDate, dateStrings)}
                    </Body>
                </Stack>
            ) : null;
        case CancellationPolicyTag.NonRefundable:
            return (
                <Tag variant="info">
                    <LocalizedText textKey={TextKey.NonRefundable} />
                </Tag>
            );
        default:
            return null;
    }
};
